/* eslint-disable max-len */
import React from 'react';
import { Box, Flex, Image } from '@qga/roo-ui/components';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';

import Banner from '@/components/Banner';
import FlightsSegment from '@/components/FlightJourneys/components/Segment';
import PillSpacer from '@/components/PillSpacer';
import Text from '@/components/Text';
import { FLIGHT_DIRECTION } from '@/constants';
import FlightOptions, {
  SelectedFlightOption,
  SetSelectedFlightOption,
} from '@/propTypes/FlightOptions';
import { isFlightsPriceForAdult as isPriceForAdult } from '@/utils/campaigns/isPriceForAdult';
import getAsset from '@/utils/getAsset';
import FlightsTerms from '../FlightsTerms/FlightsTerms';
import FlightCTA from './components/FlightCTA';
import FlightsSegmentBox from './components/FlightsSegmentBox';

const FlightList = ({
  selectedFlightOption,
  setSelectedFlightOption,
  flightDirection = FLIGHT_DIRECTION.Departing,
  flightOptions,
  hidePerPersonPrice = false,
}) => {
  const options = {
    [FLIGHT_DIRECTION.Departing]: flightOptions.outbound,
    [FLIGHT_DIRECTION.Returning]: flightOptions.inbound,
  }[flightDirection];

  const selectedOption = {
    [FLIGHT_DIRECTION.Departing]: selectedFlightOption.outbound,
    [FLIGHT_DIRECTION.Returning]: selectedFlightOption.inbound,
  }[flightDirection];

  const onSelect = {
    [FLIGHT_DIRECTION.Departing]: setSelectedFlightOption.setOutbound,
    [FLIGHT_DIRECTION.Returning]: setSelectedFlightOption.setInbound,
  }[flightDirection];

  return (
    <Box>
      <FlightsTerms showInternationalTerms />
      <Box mt="4">
        <Banner
          title="Includes 7kg carry-on baggage, 20kg checked baggage and standard seat selection"
          message="You can select your seats through &lsquo;Manage Booking&rsquo; after receiving your booking confirmation."
          iconComponent={
            <Box height="48px" width="48px" mr={['2', null, '4']}>
              <Image
                inline
                src={getAsset('/images/icons/ancil.svg')}
                alt="ancillary"
              />
            </Box>
          }
        />
      </Box>
      <Box mt={4} mb={1}>
        <Text bold textStyle="heading" fontSize="base">{`${pluralize(
          'flight',
          options.length,
          true,
        )} available`}</Text>
      </Box>
      {options && (
        <Flex flexDirection="column">
          {options.map((option, index) => {
            const priceForAdult = isPriceForAdult(option.flightSegments);
            const thisAmount = hidePerPersonPrice
              ? option.charges.total.amount
              : priceForAdult
                ? option.charges.perAdult.amount
                : option.charges.perTraveller.amount;
            const selectedAmount = hidePerPersonPrice
              ? selectedOption.charges.total.amount
              : priceForAdult
                ? selectedOption.charges.perAdult.amount
                : selectedOption.charges.perTraveller.amount;

            return (
              <Flex
                data-testid="FLIGHT-OPTION"
                key={index} // eslint-disable-line react/no-array-index-key
                mt="5"
                flexDirection="column"
              >
                <PillSpacer segments={option.flightSegments} />
                <FlightsSegmentBox isSelected={selectedOption === option}>
                  <FlightsSegment
                    cta={
                      <FlightCTA
                        isSelected={selectedOption === option}
                        priceDifference={{
                          amount: thisAmount - selectedAmount,
                          currency: option.charges.perTraveller.currency,
                        }}
                        onSelected={() => onSelect(option)}
                        priceForAdult={priceForAdult}
                        hidePerPersonPrice={hidePerPersonPrice}
                      />
                    }
                    flights={option.flightSegments}
                  />
                </FlightsSegmentBox>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

FlightList.propTypes = {
  selectedFlightOption: PropTypes.shape(SelectedFlightOption).isRequired,
  setSelectedFlightOption: PropTypes.shape(SetSelectedFlightOption).isRequired,
  flightDirection: PropTypes.string,
  hidePerPersonPrice: PropTypes.bool,
  flightOptions: PropTypes.shape(FlightOptions),
};

export default FlightList;
